import type { MeetVerdict } from "@greg-chick/shared";
import {
  normalizeSlots,
  normalizeGoodsDoc,
  mergeById,
  bookSummaryFromDoc,
  sanitizeOverlayFitDoc,
  overlayFitKeys,
  type OverlayFitDoc,
  type BookSummary,
  type AssetSlot,
  type AssetManifest,
  type AssetManifestEntry,
  type GoodsDoc,
  type GoodsIssue,
  sanitizeSiteText,
  sanitizeWorldSize,
  type AssistState,
  type AssistTurn,
  type AssistAnswer,
  type SiteText,
  type WorldSize,
  type AiProvider,
  type AiProviderOption,
  isEdgeAssetPath,
  toRelativeEdgeAsset,
  storeLinks,
  mergeBookLists,
  mergeAssetManifests,
  normalizeAgentDoc,
  sanitizeChatLogRows,
  type NpcChatLogRow,
  normalizeKbPackDoc,
  type NpcAgentDoc,
  type KbPackDoc,
  type NpcQa,
  uploadFailSay,
} from "@greg-chick/shared";

// 단일 정의는 shared. 기존 사용처 호환을 위해 별칭 재노출.
export type Manifest = AssetManifest;
export type ManifestEntry = AssetManifestEntry;

// 콘텐츠 출처(슬롯·매니페스트·book.json). 기본은 로컬 웹 dev 서버.
export function contentBase(): string {
  return (import.meta.env.VITE_CONTENT_BASE?.trim() || "http://localhost:5173").replace(/\/$/, "");
}

// 엣지 게시 API 출처(게시 POST·게시본 조회). dev 기본 :8788(8787은 다른 로컬 프로젝트와 충돌 이력).
export function adminApi(): string {
  return (import.meta.env.VITE_ADMIN_API?.trim() || "http://localhost:8788").replace(/\/$/, "");
}
export function adminToken(): string {
  return sessionStorage.getItem("gc_admin_token") ?? "";
}

/**
 * 저장이 401 로 막혔다고 화면 전체에 알린다 → 「다시 넣기」 창이 뜬다.
 *
 * ## 왜 새로고침이 아니라 이 방식인가
 * 열쇠는 **탭마다** 따로 저장된다(sessionStorage). 오래 열어 둔 탭은 옛 열쇠를 들고 있어서
 * 편집은 다 되는데 **저장할 때만** 401 이 난다 — 운영자는 벽을 다 고친 뒤에야 그걸 안다.
 * 여기서 새로고침을 시키면 **방금 고친 것이 통째로 날아간다.** 그래서 화면은 그대로 두고
 * 열쇠만 바꿔 끼우게 한다(고친 내용을 들고 곧바로 다시 저장할 수 있게).
 */
export const AUTH_FAIL_EVENT = "gc-admin-401";
export function notifyAuthFail(): void {
  window.dispatchEvent(new CustomEvent(AUTH_FAIL_EVENT));
}

/** 401 을 만난 자리에서 `throw authFail()` — 알리기와 던지기를 한 줄로(빠뜨릴 자리를 없앤다). */
export function authFail(): Error {
  notifyAuthFail();
  return new Error("401");
}

/**
 * 🔴 **다른 곳이 먼저 올렸다.** 이대로 올리면 저쪽 것이 지워진다.
 *
 * 합쳐 주지 않는다 — 무엇이 이 화면에서 바뀐 것인지 알 수가 없어서,
 * 합치면 **저쪽이 지운 것을 되살린다.** 그래서 운영자에게 무슨 일인지 말하고 맡긴다.
 *
 * 새로고침하라고 하지 않는다 — 편집하던 것이 날아간다(관리자는 저장 전 상태를 안 붙든다).
 */
export function staleFail(info: unknown): Error {
  const { current, base } = (info ?? {}) as { current?: number; base?: number };
  const 판 = typeof base === "number" && typeof current === "number" ? ` (판 ${base} → ${current})` : "";
  return new Error(
    `다른 곳에서 먼저 올렸어요${판}. 이 화면 것을 그대로 올리면 저쪽에서 올린 것이 지워져요. ` +
      "새 탭에서 관리자를 열어 무엇이 바뀌었는지 보고, 이 화면에서 바꾼 것만 다시 해 주세요.",
  );
}

/**
 * 게이트 입장 전 토큰 검증. ok=일치, bad=불일치(401), busy=틀린 시도가 많아 잠깐 막힘(429), offline=엣지 미연결.
 * 🔴 429 를 「틀렸다」로 읽지 않는다(2026-10-15) — 맞는 열쇠를 넣은 운영자에게 「안 맞아요」라고 해서 또 시도하게 만들었다.
 */
export async function checkToken(token: string): Promise<"ok" | "bad" | "busy" | "offline"> {
  try {
    const res = await fetch(`${adminApi()}/admin/check`, {
      headers: { "x-admin-token": token },
    });
    if (res.status === 429) return "busy";
    return res.ok ? "ok" : "bad";
  } catch {
    return "offline";
  }
}

/**
 * 이미지 원본을 엣지 R2 에 업로드 → **관리자에서 바로 볼 수 있는 주소**. 실패 시 throw.
 *
 * ## 🔴 왜 절대 주소로 돌려주나 (2026-08-21 신고)
 * 엣지는 `/assets/…` 상대 경로를 준다. 그런데 관리자는 **다른 출처**(admin.…)라 그대로
 * `<img src>` 에 넣으면 그림을 못 찾는다 — 화면엔 "올라갔어요"가 뜨는데 **미리보기만
 * 안 바뀌고**, 새로고침해야 보였다(그때 `loadManifest` 가 절대 주소로 바꿔 준다).
 *
 * 올리는 곳이 **아홉 군데**다(전자책·굿즈·AR·움직임·소품…). 한 곳씩 고치면 반드시 빠뜨린다.
 * 그래서 **여기서 한 번** 바꿔 준다 — 상태에 들어가는 형태가 `loadManifest` 와 같아진다.
 * 게시할 때 `publishAssets` 가 다시 상대 경로로 되돌리므로 저장되는 값은 그대로다.
 */
export async function uploadAsset(slotKey: string, file: File): Promise<string> {
  const res = await fetch(`${adminApi()}/admin/upload?slot=${encodeURIComponent(slotKey)}`, {
    method: "POST",
    headers: {
      "content-type": file.type || "application/octet-stream",
      "x-admin-token": adminToken(),
    },
    body: file,
  });
  if (!res.ok) {
    /**
     * 🔴 **왜 안 올라갔는지 말한다.** 예전엔 `upload 400` 만 던져서 화면이
     * 「저장 실패(엣지 확인)」이라고 했다 — 스캐너로 뜬 60MB 그림을 올린 운영자는
     * 무엇이 문제인지 알 수가 없다. 가장 흔한 실수인데 가장 안 알려 주던 자리다.
     */
    const body = (await res.json().catch(() => ({}))) as { error?: unknown; bytes?: unknown };
    throw new Error(uploadFailSay(res.status, body));
  }
  const out = (await res.json()) as { url: string };
  return absArtUrl(out.url) ?? out.url;
}

/**
 * 🔤 **그림에서 글자 읽기** — 페이지 그림을 보고 자막·의성어 초안을 받는다.
 *
 * 그림책 원고는 대개 글이 그림 안에 박혀 있다. 그걸 손으로 다시 타이핑하는 대신 읽어서 채운다.
 * **초안이지 정답이 아니다** — 받은 값은 칸에만 넣고, 저장은 운영자가 [웹에 올리기]로 한다.
 */
export interface BookOcrText {
  subtitle_ko: string;
  subtitle_en: string;
  motionText_ko: string;
  motionText_en: string;
  found: boolean;
}

export async function readPageText(
  bookId: string,
  pageId: string,
  hint?: string,
): Promise<BookOcrText> {
  // 서버에 못 닿는 경우도 사람 말로 — 브라우저 기본 문구("Failed to fetch")가 그대로 뜨면
  // 운영자는 자기가 뭘 잘못했는지 알 수 없다.
  const res = await fetch(`${adminApi()}/admin/book/ocr`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ bookId, pageId, hint }),
  }).catch(() => {
    throw new Error("서버에 연결하지 못했어요. 잠시 뒤 다시 눌러 주세요.");
  });
  if (res.status === 401) throw authFail();
  if (res.ok) return (await res.json()) as BookOcrText;
  // 사람 말로 — 무엇을 하면 되는지까지 말해 준다
  const code = await res
    .json()
    .then((b) => (b as { error?: string }).error)
    .catch(() => undefined);
  const MSG: Record<string, string> = {
    no_image: "이 페이지에 그림이 없어요. 먼저 그림을 올린 뒤 눌러 주세요.",
    ai_unavailable: "AI 키가 없어요. [⚙️ 설정·백업]의 「AI 키」에서 넣어 주세요.",
    rate_limited: "오늘 사용량을 다 썼어요. 내일 다시 해주세요.",
    ai_failed: "AI 가 답하지 못했어요. 잠시 뒤 다시 눌러 주세요.",
    bad_page: "페이지를 먼저 저장한 뒤에 눌러 주세요.",
  };
  throw new Error(MSG[code ?? ""] ?? `글자 읽기 실패 (${res.status})`);
}

/** AI 나레이션 생성(TTS) — 자막 텍스트 → mp3 Blob. 실패 시 사람 말 에러 throw. */
export async function generateNarration(text: string, voice: string): Promise<Blob> {
  const res = await fetch(`${adminApi()}/admin/tts`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ text, voice }),
  });
  if (res.ok) return res.blob();
  if (res.status === 401) throw authFail();
  // 엣지가 사람 말 메시지를 준다(키 없음·너무 김 등) — 그대로 표시
  const msg = await res
    .json()
    .then((b) => (b as { message?: string }).message)
    .catch(() => undefined);
  throw new Error(msg || `음성 생성 실패(${res.status})`);
}

/**
 * 그림 자리 정의를 읽는다.
 *
 * 🔴 **못 받아 와도 던지지 않는다.** 예전엔 네트워크가 끊기면 여기서 던졌고, 부르는 쪽이
 * `.catch` 를 안 달아 두어 **화면이 「불러오는 중…」에서 영영 멈췄다** — 브라우저 콘솔에만
 * 「처리되지 않은 거부」가 조용히 쌓였다(2026-08-27, 화면 열어 보기 검사가 잡았다).
 *
 * 빈 목록이면 화면이 「자리 정의를 못 불러왔어요」를 띄운다 — 그게 사실이다.
 */
export async function loadSlots(): Promise<AssetSlot[]> {
  try {
    const res = await fetch(`${contentBase()}/content/asset-slots.json`, { cache: "no-cache" });
    if (!res.ok) return [];
    return normalizeSlots((await res.json()) as unknown);
  } catch {
    return []; // 서버가 없다 — 화면은 떠야 한다(불변식 3)
  }
}

/**
 * 상대 경로(`/art/...`)를 **웹 주소 기준**으로 바꾼다 — 관리자에서만.
 *
 * ## 왜 필요한가
 * 매니페스트에는 `/art/buildings/hanok/front.png` 처럼 상대 경로가 들어 있다. 웹(:5173)에서는
 * 자기 주소 아래에 그 파일이 있으니 그대로 열린다. 그런데 **관리자는 주소가 다르다**(:5174).
 * 그대로 `<img src>` 에 넣으면 `localhost:5174/art/...` 를 찾다가 404 —
 * "✓ 그림 있음" 배지는 뜨는데 **미리보기만 깨진 그림**으로 보인다(장식 17칸 전부 그랬다).
 *
 * 열쇠(`//`·`http`)가 이미 붙은 주소는 손대지 않는다 — 엣지·R2 에 올린 것들.
 */
export function absArtUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  // 올린 그림(`/assets/…`)은 **엣지**가 서빙한다 — 웹 주소를 붙이면 관리자 미리보기가 404 다.
  // 저장 형태는 상대 경로 하나뿐이고(shared/assetUrl.ts), 어느 쪽에 붙일지는 읽는 쪽이 안다.
  if (isEdgeAssetPath(url)) return `${adminApi()}${url}`;
  return url.startsWith("/") && !url.startsWith("//") ? `${contentBase()}${url}` : url;
}

/**
 * 절대 주소로 바꿔 둔 것을 **원래 상대 경로로 되돌린다** — 게시 직전에.
 *
 * 안 되돌리면 `http://localhost:5173/art/...` 가 그대로 게시돼서, 배포된 웹이 운영자 PC 를
 * 가리키게 된다(아무에게도 안 보인다). 들어올 때 한 곳, 나갈 때 한 곳 — 여기 둘뿐이다.
 */
function relArtUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  // 올린 그림은 호스트를 통째로 떼어낸다(운영자 PC 주소가 게시본에 박히면 아이 화면이 깨진다).
  const rel = toRelativeEdgeAsset(url);
  if (rel !== url) return rel;
  const base = contentBase();
  return url.startsWith(`${base}/`) ? url.slice(base.length) : url;
}

/** 매니페스트 전체에 url/prev 변환을 적용. */
function mapArtUrls(
  assets: Record<string, ManifestEntry>,
  f: (u: string | undefined) => string | undefined,
): Record<string, ManifestEntry> {
  const out: Record<string, ManifestEntry> = {};
  for (const [k, e] of Object.entries(assets)) {
    out[k] = { ...e, url: f(e.url) as string, ...(e.prev ? { prev: f(e.prev) } : {}) };
  }
  return out;
}

/** 정적 기본 아트(항상 존재) **아래에** 게시본을 덮어쓴 병합 매니페스트를 편집 베이스로 쓴다.
 *  → 기본 아트(바닥·장식·타일·배경)가 항상 포함돼, 게시 시 누락으로 사라지지 않는다.
 *  운영자가 올린/덮어쓴 키는 게시본이 우선. */
export async function loadManifest(): Promise<Manifest> {
  const get = async (url: string): Promise<Manifest | null> => {
    try {
      const res = await fetch(url, { cache: "no-cache" });
      if (res.ok) {
        const m = (await res.json()) as Manifest;
        if (m && typeof m.assets === "object") return m;
      }
    } catch {
      /* 무시 */
    }
    return null;
  };
  const [edge, stat] = await Promise.all([
    get(`${adminApi()}/api/asset-manifest`),
    get(`${contentBase()}/content/asset-manifest.json`),
  ]);
  // 합치기는 **웹과 같은 함수**(shared) — 관리자에 보이는 것과 아이 화면이 갈리면 안 된다.
  // 「지움」으로 표시한 자리는 여기서도 떨어져서, 지운 것이 관리자에도 안 보인다.
  //
  // 정적 목록은 게시할 때 **되살아남을 자리를 찾는 데** 다시 쓴다(staticAssetKeys).
  staticKeys = new Set(Object.keys(stat?.assets ?? {}));
  seenVersion.set("manifest", edge?.version ?? 0);
  // 관리자 화면에서 바로 열 수 있는 주소로. 게시할 때 publishAssets 가 원래대로 되돌린다.
  return {
    version: edge?.version ?? stat?.version ?? 0,
    // 🙈 「웹에서 내린」 것은 **관리자에는 남긴다** — 안 남기면 다시 올릴 방법이 없다.
    //    아이 화면(apps/web)은 이 옵션을 켜지 않는다.
    assets: mapArtUrls(
      mergeAssetManifests(stat?.assets ?? {}, edge?.assets ?? {}, { keepOffWeb: true }),
      absArtUrl,
    ),
  };
}

/** 매니페스트 게시(FR-ADMIN-06): 엣지에 저장 → 앱이 /api/asset-manifest 로 즉시 반영. */
/**
 * 마지막으로 불러온 **정적 기본 매니페스트**의 키들.
 *
 * 게시본에서 키를 빼는 것만으로는 안 지워진다 — 웹이 정적 기본 위에 게시본을 덮으므로
 * 빠진 자리는 **정적 그림이 되살아난다.** 그래서 「원래 정적에 있었는데 지금은 없는 자리」를
 * 찾아 `hidden` 표시를 만들어 보낸다. 그 목록이 이것이다.
 */
let staticKeys = new Set<string>();

/**
 * 마지막으로 **엣지에서 불러온 판 번호** — 문서별로.
 *
 * 🔴 게시는 통째로 바꾸기라, 다른 곳(다른 탭·다른 사람)이 먼저 올린 뒤에 이 화면 것을
 * 올리면 저쪽 것이 조용히 지워진다. 이 번호를 같이 보내면 엣지가 막아 준다.
 * 정적 폴백의 판은 쓰지 않는다 — 엣지에 아무것도 없으면 0 이 맞다.
 *
 * 열쇠: `manifest` · `goods` · `book:<id>` · `map:<zone>`
 */
const seenVersion = new Map<string, number>();
/** 올린 뒤 새 판을 기억한다 — 안 그러면 두 번째 게시가 늘 막힌다. */
export function noteDocVersion(what: string, version: number): void {
  seenVersion.set(what, version);
}

/**
 * 기억을 지운다 — **서버 쪽이 우리 모르게 바뀐 뒤**에 쓴다(지우기·스냅샷 복원).
 * 남겨 두면 다음 게시가 「다른 곳에서 먼저 올렸어요」로 막힌다. 아무도 안 올렸는데.
 */
export function forgetDocVersion(what?: string): void {
  if (what === undefined) seenVersion.clear();
  else seenVersion.delete(what);
}
/**
 * 🔴 **「모른다」와 「아직 아무것도 안 올라감(0판)」은 다르다.**
 *
 * 안 가르고 둘 다 0 으로 보내면, 불러온 적 없는 문서를 올릴 때 **멀쩡한 게시가 막힌다**
 * (서버는 5판인데 0 을 보내니 「앞질러 올라갔다」로 읽는다). 실제로 두 자리에서 그랬다:
 * 지운 책 되살리기, 그리고 스냅샷 복원 뒤의 아무 게시.
 *
 * 모르면 **안 보낸다** — 엣지는 안 보내면 막지 않는다(그 편이 안전하다: 막아서 못 고치게
 * 하는 것보다, 덮어쓸 수 있게 두고 사람이 판단하는 쪽).
 */
export const baseOf = (what: string): number | undefined => seenVersion.get(what);
/**
 * 몸통을 안 건드리려고 판을 헤더로 받는다(책·맵·굿즈). 못 읽으면 0 — 그럼 막지 않는다.
 *
 * 🔴 내보내는 이유: 맵 편집기가 **제 손으로** 불러오고 저장한다(편집 중 상태를 붙들어야 해서).
 * 판을 기억하는 곳은 그래도 **여기 하나**여야 한다.
 */
export function noteVersionHeader(what: string, res: Response): void {
  const v = Number(res.headers.get("X-Doc-Version"));
  seenVersion.set(what, Number.isFinite(v) ? v : 0);
}

/** 지운 자리에 남길 「지움」 표시 — 정적 기본에 있던 자리만. */
export function tombstones(
  assets: Record<string, ManifestEntry>,
  known: Set<string> = staticKeys,
): Record<string, ManifestEntry> {
  const out: Record<string, ManifestEntry> = {};
  for (const k of known) if (!assets[k]) out[k] = { url: "", hidden: true };
  return out;
}

export async function publishAssets(
  assets: Record<string, ManifestEntry>,
): Promise<{ version: number; count: number }> {
  const res = await fetch(`${adminApi()}/admin/publish`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    // 미리보기용으로 붙여 둔 웹 주소를 떼고 상대 경로로 되돌려 보낸다 —
    // 안 떼면 배포된 웹이 운영자 PC(localhost)를 가리킨다.
    // 지운 자리에는 「지움」 표시를 같이 보낸다(안 보내면 정적 그림이 되살아난다).
    // 🔴 「내가 불러온 판」을 같이 보낸다 — 그 사이에 다른 곳이 올렸으면 엣지가 막는다
    body: JSON.stringify({
      assets: { ...mapArtUrls(assets, relArtUrl), ...tombstones(assets) },
      ...(baseOf("manifest") !== undefined ? { baseVersion: baseOf("manifest") } : {}),
    }),
  });
  if (res.status === 401) throw authFail();
  if (res.status === 409) throw staleFail(await res.json().catch(() => ({})));
  if (!res.ok) throw new Error(String(res.status));
  const out = (await res.json()) as { version: number; count: number };
  seenVersion.set("manifest", out.version); // 이제 이게 내가 아는 판이다
  return out;
}

export interface BookDoc {
  pages: Array<Record<string, unknown>>;
  [k: string]: unknown;
}

/** 목록 카드 메타 — 정의는 packages/shared(웹 고르기 화면·엣지와 같은 것). */
export type BookInfo = BookSummary;

/** 저장된 전자책 목록(게시본). 비었으면 정적 단일책 1권으로 폴백. */
/**
 * 관리자에 보여줄 전자책 목록.
 *
 * ## 🔴 폴백이 웹과 같은 곳을 봐야 한다
 * 게시본(D1)이 비어 있으면 저장소 파일로 떨어지는데, 예전엔 **`book.json`(한 권짜리 옛 파일)**
 * 을 읽었다. 그런데 웹은 **`books.json`(여러 권 목록)** 을 본다. 그래서 운영자에게는
 * `main` 한 권만 보이고, **아이 화면에 멀쩡히 있는 「나무 위 도서관」은 목록에 없어서 고칠 수가
 * 없었다**(2026-08-20 신고). 화면도 에러도 멀쩡해서 원인을 짚기 어려운 종류다.
 *
 * 순서: ①게시본 → ②웹과 같은 목록(books.json) → ③옛 한 권짜리(book.json)
 * ③은 아주 오래된 콘텐츠를 위한 마지막 그물이다.
 */
export async function listBooks(): Promise<BookInfo[]> {
  const get = async (url: string): Promise<BookInfo[]> => {
    try {
      const r = await fetch(url, { cache: "no-cache" });
      if (!r.ok) return [];
      return ((await r.json()) as { books?: BookInfo[] }).books ?? [];
    } catch {
      return [];
    }
  };
  // 웹이 읽는 바로 그 파일 + 게시본 — **웹과 같은 함수로 합친다**.
  // 여기가 갈리면 "아이 화면엔 있는데 관리자엔 없다"가 된다(둘 다 겪었다).
  const [published, repo] = await Promise.all([
    get(`${adminApi()}/api/books`),
    get(`${contentBase()}/content/books.json`),
  ]);
  const merged = mergeBookLists(repo, published);
  if (merged.length > 0) return merged;

  // 아주 오래된 한 권짜리 book.json — 마지막 그물
  try {
    const r = await fetch(`${contentBase()}/content/book.json`, { cache: "no-cache" });
    if (r.ok) {
      const d = (await r.json()) as Record<string, unknown>;
      return [bookSummaryFromDoc(typeof d.id === "string" ? d.id : "main", d)];
    }
  } catch {
    /* 없으면 빈 목록 */
  }
  return [];
}

export async function loadBookDoc(id?: string): Promise<BookDoc> {
  const q = id ? `?id=${encodeURIComponent(id)}` : "";
  const statics = id
    ? [`${contentBase()}/content/book.${encodeURIComponent(id)}.json`]
    : [`${contentBase()}/content/book.json`];
  const edgeUrl = `${adminApi()}/api/book${q}`;
  for (const url of [edgeUrl, ...statics]) {
    try {
      const r = await fetch(url, { cache: "no-cache" });
      if (r.ok) {
        // 🔴 엣지에서 받은 것만 판이 있다. 저장소 폴백을 읽었으면 0 — 그럼 막지 않는다
        if (url === edgeUrl) noteVersionHeader(`book:${id ?? "main"}`, r);
        const d = (await r.json()) as BookDoc;
        if (d && Array.isArray(d.pages)) return d;
      }
    } catch {
      /* 다음 후보 */
    }
  }
  return { pages: [] };
}

/** 전자책 저장(이름별). id=슬러그. */
export async function publishBook(
  id: string,
  doc: BookDoc,
): Promise<{ id: string; title: string; version: number; pages: number }> {
  const res = await fetch(`${adminApi()}/admin/book?id=${encodeURIComponent(id)}`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    // 🔴 「내가 불러온 판」 — 같은 책을 둘이 고치면 뒤에 누른 쪽이 앞의 것을 통째로 덮는다
    body: JSON.stringify({
      ...doc,
      ...(baseOf(`book:${id}`) !== undefined ? { baseVersion: baseOf(`book:${id}`) } : {}),
    }),
  });
  if (res.status === 401) throw authFail();
  if (res.status === 409) throw staleFail(await res.json().catch(() => ({})));
  if (!res.ok) throw new Error(String(res.status));
  const out = (await res.json()) as { id: string; title: string; version: number; pages: number };
  noteDocVersion(`book:${id}`, out.version);
  return out;
}

/** 전자책 삭제. */
export async function deleteBook(id: string): Promise<void> {
  const res = await fetch(`${adminApi()}/admin/book?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  /**
   * 🔴 **지웠으면 판도 잊는다.**
   *
   * 안 잊으면 [↩ 되살리기]가 막힌다: 기억은 지우기 전 판(예: 3)인데 서버에는 줄이 없어
   * 지금 판이 0 이다 → 「다른 곳에서 먼저 올렸어요」로 거절된다. 되살리기는 **지운 사람이
   * 곧바로 누르는 것**이라 그 말은 틀린 데다, 그 길이 막히면 책을 못 되찾는다.
   */
  forgetDocVersion(`book:${id}`);
}

// ── 환경 스냅샷(이름으로 저장·복원) ─────────────────────────
export interface SnapshotInfo {
  name: string;
  created_at: string;
}
export async function listSnapshots(): Promise<SnapshotInfo[]> {
  try {
    const res = await fetch(`${adminApi()}/admin/snapshots`, {
      headers: { "x-admin-token": adminToken() },
    });
    if (!res.ok) return [];
    return ((await res.json()) as { snapshots: SnapshotInfo[] }).snapshots ?? [];
  } catch {
    return [];
  }
}
export async function saveSnapshot(name: string): Promise<{ zones: number }> {
  const res = await fetch(`${adminApi()}/admin/snapshot?name=${encodeURIComponent(name)}`, {
    method: "POST",
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as { zones: number };
}
export async function restoreSnapshot(
  name: string,
): Promise<{ applied: { manifest: boolean; book: boolean; maps: string[] } }> {
  const res = await fetch(`${adminApi()}/admin/snapshot/restore?name=${encodeURIComponent(name)}`, {
    method: "POST",
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  /**
   * 🔴 복원은 **서버 쪽 판을 전부 올린다.** 우리가 기억하던 판은 그 순간 낡은 것이 된다 —
   * 안 잊으면 복원 직후의 아무 게시가 「다른 곳에서 먼저 올렸어요」로 막힌다.
   * 아무도 안 올렸는데(운영자 자신이 복원한 것이다).
   */
  forgetDocVersion();
  return (await res.json()) as { applied: { manifest: boolean; book: boolean; maps: string[] } };
}
export async function deleteSnapshot(name: string): Promise<void> {
  // 응답을 확인하지 않으면 CORS·401 실패가 조용히 사라져 운영자에겐 "고장난 버튼"으로만 보인다.
  const res = await fetch(`${adminApi()}/admin/snapshot?name=${encodeURIComponent(name)}`, {
    method: "DELETE",
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(`삭제 실패 (${res.status})`);
}

export interface BookPageRef {
  id: string;
  title: string;
}
/**
 * 🔴 **「없다」와 「못 읽었다」를 가른다.** 전엔 실패하면 `[]` 를 돌려줬고, 화면은 그걸
 * 받아 「정적 기본 책 **0쪽**」이라고 말했다 — 사실이 아니다(못 읽은 것이다).
 * 「0」은 「안 셌다」일 수 있다는 것이 이 저장소가 반복해서 배우는 것이다.
 *
 * 못 읽었으면 `null` — 부르는 쪽이 다른 말을 할 수 있게.
 */
export async function loadBookPages(): Promise<BookPageRef[] | null> {
  try {
    const res = await fetch(`${contentBase()}/content/book.json`, { cache: "no-cache" });
    if (!res.ok) return null;
    const data = (await res.json()) as { pages?: { id?: string; subtitle_ko?: string }[] };
    return (data.pages ?? [])
      .filter((p) => typeof p.id === "string")
      .map((p) => ({ id: p.id!, title: p.subtitle_ko ?? p.id! }));
  } catch {
    return null; // 못 물어봤다 — 「0쪽」이라고 말하면 안 된다
  }
}

// ── 굿즈 상세(docs/build/16 P1) ──────────────────────────────
/** 게시본 조회. 미게시(404)면 정적 폴백을 읽는다 — 첫 운영 때 목록이 비어 보이지 않게. */
/**
 * 🛍️ 굿즈 문서 — **저장소 목록 위에 게시본을 덮어쓴다**(웹과 같은 규칙).
 *
 * 🔴 예전엔 **게시본이 있으면 저장소 파일을 안 봤다.** 그러면 개발자가
 * `content/goods.json` 에 넣은 상품이 관리자에 **아예 안 보인다** — 운영자는
 * 그걸 모르고 저장하고, 그 순간 아이 화면에서도 사라진다.
 * 책에서 난 사고와 같은 모양이다(2026-08-21 — 빈 책 한 권이 진짜 책 두 권을 지움).
 * 합치는 규칙은 shared 한 곳(`mergeById`).
 */
export async function loadGoodsDoc(): Promise<GoodsDoc> {
  const [fromEdge, stat] = await Promise.all([
    fetch(`${adminApi()}/api/goods`, { cache: "no-store" }).catch(() => null),
    fetch(`${contentBase()}/content/goods.json`, { cache: "no-cache" }).catch(() => null),
  ]);
  if (fromEdge?.ok) noteVersionHeader("goods", fromEdge);
  else seenVersion.set("goods", 0); // 아직 아무것도 안 올라갔다
  const published = fromEdge?.ok ? normalizeGoodsDoc(await fromEdge.json()).products : [];
  const repo = stat?.ok ? normalizeGoodsDoc(await stat.json()).products : [];
  return { products: mergeById(repo, published) };
}

/** 문서 전체 게시. 엣지가 검증에 실패하면 issues 를 그대로 올려 폼에서 필드별로 표시한다. */
export async function publishGoods(doc: GoodsDoc): Promise<{ version: number; count: number }> {
  const res = await fetch(`${adminApi()}/admin/goods`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    // 🔴 굿즈도 문서 통째로 저장한다 — 앞질러 올라간 것을 덮지 않게 판을 같이
    body: JSON.stringify({
      ...doc,
      ...(baseOf("goods") !== undefined ? { baseVersion: baseOf("goods") } : {}),
    }),
  });
  if (res.status === 401) throw authFail();
  if (res.status === 409) throw staleFail(await res.json().catch(() => ({})));
  if (res.status === 400) {
    const body = (await res.json()) as { issues?: GoodsIssue[] };
    const err = new Error("invalid") as Error & { issues?: GoodsIssue[] };
    err.issues = body.issues ?? [];
    throw err;
  }
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as { version: number; count: number };
}

// ── 굿즈 AI 초안(docs/build/16 §4) ───────────────────────────
export interface GoodsDraftOut {
  provider: AiProvider;
  providerLabel: string;
  draft: {
    tagline_child_ko: string;
    desc_guardian_ko: string;
    badge: string | null;
    tags: string[];
    notes: string[];
  };
  review: { safety: "pass" | "revise"; factuality: "pass" | "revise" };
  remaining: number;
}

/** 쓸 수 있는 AI 공급자(엣지에 키가 있는 것만). 빈 배열이면 AI 기능 자체를 그리지 않는다. */
export async function goodsAiProviders(): Promise<AiProviderOption[]> {
  const res = await fetch(`${adminApi()}/admin/check`, {
    headers: { "x-admin-token": adminToken() },
  }).catch(() => null);
  if (!res?.ok) return [];
  const body = (await res.json()) as { features?: { goodsAiProviders?: AiProviderOption[] } };
  return body.features?.goodsAiProviders ?? [];
}

/** 초안 생성. 이미지는 업로드가 먼저(슬롯 키로 참조) — 엣지가 R2 에서 읽어 Claude 에 넘긴다. */
export async function draftGoods(input: {
  name_ko: string;
  imageSlots: string[];
  hint?: string;
  provider?: AiProvider;
}): Promise<GoodsDraftOut> {
  const res = await fetch(`${adminApi()}/admin/goods/draft`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(input),
  });
  if (res.ok) return (await res.json()) as GoodsDraftOut;
  // 실패 이유를 운영자 말로 — 삼키면 "왜 안 되지"로 끝난다.
  const reason = ((await res.json().catch(() => ({}))) as { error?: string }).error;
  const msg: Record<string, string> = {
    ai_unavailable: "AI 기능이 꺼져 있어요(서버에 API 키가 하나도 설정되지 않았어요).",
    provider_unavailable: "고른 AI 의 키가 서버에 없어요. 다른 AI 를 골라주세요.",
    provider_failed: "고른 AI 가 응답에 실패했어요. 다른 AI 로 시도해 보세요.",
    daily_limit: "오늘 AI 초안 사용량을 다 썼어요. 내일 다시 시도해 주세요.",
    image_required: "이미지를 먼저 올려 주세요. 사진을 보고 글을 쓰거든요.",
    name_required: "상품명을 먼저 입력해 주세요.",
  };
  throw new Error(msg[reason ?? ""] ?? `초안 생성 실패 (${res.status})`);
}

// ── 운영자 관리 시크릿(AI 공급자 키) — docs/build/00 S4-3 ─────
export interface SecretStatus {
  name: string;
  source: "env" | "stored" | "none";
  hint?: string; // 끝 4자만(API 키용)
  /** 설정 항목(모델 id)만 실값. API 키는 서버가 절대 내려보내지 않는다. */
  value?: string;
}

export async function loadSecrets(): Promise<{ enabled: boolean; secrets: SecretStatus[] }> {
  const res = await fetch(`${adminApi()}/admin/secrets`, {
    headers: { "x-admin-token": adminToken() },
  }).catch(() => null);
  if (!res?.ok) return { enabled: false, secrets: [] };
  return (await res.json()) as { enabled: boolean; secrets: SecretStatus[] };
}

export async function saveSecret(name: string, value: string): Promise<SecretStatus> {
  const res = await fetch(`${adminApi()}/admin/secrets`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ name, value }),
  });
  if (res.ok)
    return { name, source: "stored", hint: ((await res.json()) as { hint: string }).hint };
  const reason = ((await res.json().catch(() => ({}))) as { error?: string }).error;
  const msg: Record<string, string> = {
    secrets_disabled:
      "서버에 암호화 키(SECRETS_KEY)가 없어 저장할 수 없어요. 개발자에게 1회 설정을 요청하세요.",
    fixed_by_env: "이 키는 서버 시크릿으로 고정돼 있어 여기서 바꿀 수 없어요.",
    empty_value: "값을 입력해 주세요.",
    too_long: "값이 너무 길어요.",
    bad_name: "저장할 수 없는 항목이에요.",
  };
  throw new Error(msg[reason ?? ""] ?? `저장 실패 (${res.status})`);
}

export async function deleteSecret(name: string): Promise<void> {
  const res = await fetch(`${adminApi()}/admin/secrets?name=${encodeURIComponent(name)}`, {
    method: "DELETE",
    headers: { "x-admin-token": adminToken() },
  });
  // 숫자만 던지면 화면에 "401"만 뜬다 — 운영자가 뭘 해야 할지 모른다.
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(`삭제 실패 (${res.status})`);
}

/** 공급자에 실제로 있는 모델 id 목록. 가격은 오지 않는다(어떤 공급자도 API 로 안 준다). */
export async function fetchLiveModels(provider: string): Promise<string[]> {
  const res = await fetch(
    `${adminApi()}/admin/ai/models?provider=${encodeURIComponent(provider)}`,
    {
      headers: { "x-admin-token": adminToken() },
    },
  );
  if (res.ok) return ((await res.json()) as { models: string[] }).models;
  const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (body.error === "provider_unavailable") throw new Error("이 AI 의 키가 없어요.");
  throw new Error(body.message ?? `목록을 못 가져왔어요 (${res.status})`);
}

// ── 존 맵 로드·게시(굿즈 자동 진열 등에서 재사용) ──────────────
/** 존 맵 로드: 게시본(엣지) 우선 → 정적 폴백. 실패 시 null. */
export async function loadZoneMap(zone: string): Promise<unknown | null> {
  const edgeUrl = `${adminApi()}/api/map/${zone}`;
  for (const url of [edgeUrl, `${contentBase()}/maps/${zone}.json`]) {
    try {
      const r = await fetch(url, { cache: "no-cache" });
      if (r.ok) {
        // 🔴 엣지에서 받은 것만 판이 있다. 저장소 폴백이면 0 — 그럼 막지 않는다
        if (url === edgeUrl) noteVersionHeader(`map:${zone}`, r);
        return await r.json();
      }
    } catch {
      /* 다음 후보 */
    }
  }
  return null;
}

/** 존 맵 게시 → 월드가 /api/map/:zone 으로 즉시 반영. 실패는 던진다(호출부가 표시). */
export async function publishZoneMap(zone: string, map: unknown): Promise<{ version: number }> {
  /**
   * 🔴 맵은 **몸통이 Tiled 문서**라 판을 몸통에 못 넣는다 — 주소로 보낸다.
   * 안 보내면 엣지가 막지 않으므로, 못 불러온 경우(0)에도 그냥 보낸다.
   */
  const base = baseOf(`map:${zone}`);
  const q = base === undefined ? "" : `?base=${base}`;
  const res = await fetch(`${adminApi()}/admin/map/${zone}${q}`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(map),
  });
  if (res.status === 401) throw authFail();
  if (res.status === 409) throw staleFail(await res.json().catch(() => ({})));
  if (!res.ok) throw new Error(`진열 실패 (${res.status})`);
  const out = (await res.json()) as { version: number };
  noteDocVersion(`map:${zone}`, out.version);
  return out;
}

// ── 랠리 이벤트 운영 설정(FR-RALLY) — 선착순 인원·기간·운영시간·상품신청 폼 ──────────────
export interface RallyEventDoc {
  start?: string;
  end?: string;
  hours?: { open: string; close: string };
  winners: number;
  claimFormUrl?: string;
}
export interface RallyPointDoc {
  id: string; // [a-z0-9_]
  title_ko: string;
  lat: number;
  lng: number;
  radius_m: number; // 서버가 10~500m 로 클램프
  verify: "geofence_ar" | "onsite_qr";
  ar_asset?: string;
}
export interface RallyEventInfo {
  event: RallyEventDoc; // 유효 병합본(정적 기본 + 오버라이드)
  override: Partial<RallyEventDoc> | null; // 관리자가 저장한 값(없으면 null)
  defaults: RallyEventDoc; // 정적 기본(rally.data.json)
  /**
   * 완주·지급 + **스탬프 현황**.
   * 완주 수만 보면 「아무도 안 왔다」와 「왔는데 못 끝냈다」가 구분이 안 된다.
   */
  stats: {
    completions: number;
    awarded: number;
    stamps: number;
    people: number;
    byPoint: { pointId: string; n: number }[];
  } | null;
  points: RallyPointDoc[]; // 유효 지점(오버라이드 → 정적)
  qrTokens: Record<string, string>; // 현장 QR 토큰 — **운영자만** 조회 가능
  pointsOverridden: boolean; // true=관리자가 지점을 편집함, false=정적 사용 중
}

/** 랠리 이벤트 설정 + 완주 통계 조회. 실패는 던진다(호출부 ActionButton 이 표시). */
export async function getRallyEvent(): Promise<RallyEventInfo> {
  const res = await fetch(`${adminApi()}/admin/rally-event`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as RallyEventInfo;
}

/** 랠리 이벤트 설정 저장(부분 오버라이드). points/qrTokens 를 안 보내면 기존 값 유지. */
export async function saveRallyEvent(
  doc: Partial<RallyEventDoc>,
  extra?: { points?: RallyPointDoc[]; qrTokens?: Record<string, string> },
): Promise<RallyEventDoc> {
  const res = await fetch(`${adminApi()}/admin/rally-event`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ ...doc, ...extra }),
  });
  if (res.status === 400) {
    const b = (await res.json().catch(() => ({}))) as { error?: string };
    if (b.error === "start_after_end")
      throw new Error("시작이 종료보다 늦어요. 기간을 다시 확인해 주세요.");
    throw new Error("입력값을 확인해 주세요.");
  }
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return ((await res.json()) as { event: RallyEventDoc }).event;
}

/** 완주자 명단 CSV 다운로드(개인정보 없음: 순위·완주코드·시각·지급여부). */
export async function downloadRallyCsv(): Promise<void> {
  const res = await fetch(`${adminApi()}/api/admin/rally.csv`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "rally-completions.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── 마을 소식(커뮤니티) — 운영자 발행 읽기 콘텐츠(UGC 없음) ──────────────
export interface NoticeDoc {
  id: string; // [a-z0-9_]
  title: string;
  body: string;
  date?: string; // ISO8601
  pinned?: boolean;
}

/** 소식 목록 + 문의 폼 주소 조회(편집용). 실패는 던진다. */
export async function getNotices(): Promise<{
  notices: NoticeDoc[];
  contactUrl: string;
  /** 🏠 기본 화상 방 — 행사·수업에 따로 안 넣으면 여기로 열린다. */
  defaultMeetUrl: string;
}> {
  const res = await fetch(`${adminApi()}/admin/notices`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  const j = (await res.json()) as {
    notices?: NoticeDoc[];
    contactUrl?: string;
    defaultMeetUrl?: string;
  };
  return {
      notices: j.notices ?? [],
      contactUrl: j.contactUrl ?? "",
      defaultMeetUrl: j.defaultMeetUrl ?? "",
    };
}

/** 소식 목록 + 문의 폼 주소 저장(전체 교체) → 웹 커뮤니티에 즉시 반영. 실패는 던진다. */
export async function saveNotices(
  notices: NoticeDoc[],
  contactUrl?: string,
  defaultMeetUrl?: string,
): Promise<number> {
  const res = await fetch(`${adminApi()}/admin/notices`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ notices, contactUrl, defaultMeetUrl }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return ((await res.json()) as { count: number }).count;
}

// ── 행사 일정(FR-EVENT) — 운영자 편집(게시본이 정적 events.json 을 덮어씀) ──────────────
export interface EventDoc {
  title: string;
  start?: string; // ISO8601
  end?: string;
  applyUrl?: string; // 외부 폼(보호자 작성)
  /**
   * 🎥 무대. `meetUrl` 은 운영자가 붙여넣는 **회의 주소**(웨일온·줌·구글밋 무엇이든) —
   * 새 창으로 연다. `jitsi` 는 옛 방식(끼워 넣기)이라 `meetUrl` 이 있으면 안 쓴다.
   * 모양은 shared `EventStage` 와 같아야 한다 — 갈리면 관리자에서 넣은 값이 조용히 안 나간다.
   */
  stage?: { meetUrl?: string; jitsi?: string; youtubeLive?: string; capacity?: number };
  /** 아이 화면에 보일까(관리자 스위치). 없으면 보이는 것 — 이미 올려둔 행사가 사라지면 안 된다. */
  visible?: boolean;
}

/** 행사 목록 조회(편집용). 실패는 던진다. */
export async function getEvents(): Promise<EventDoc[]> {
  const res = await fetch(`${adminApi()}/admin/events`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return ((await res.json()) as { events?: EventDoc[] }).events ?? [];
}

/** 행사 목록 저장(전체 교체) → 랜딩 배너·커뮤니티에 즉시 반영. 실패는 던진다. */
export async function saveEvents(events: EventDoc[]): Promise<number> {
  const res = await fetch(`${adminApi()}/admin/events`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ events }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return ((await res.json()) as { count: number }).count;
}

// ── NPC 대사 풀(마을 생활) — 승인된 것만 아이에게 보인다 ──────────────
export type NpcLineKind = "idle" | "poi" | "pair" | "cheer";
export type NpcLineStatus = "pending" | "approved" | "rejected";
export interface NpcLineDoc {
  id: string;
  zone: string;
  kind: NpcLineKind;
  text: string;
  npc?: string;
  poi?: string;
  reply?: string;
  status: NpcLineStatus;
  source?: "ai" | "manual";
}

/** 대사 전체 조회(대기·승인·거절 모두 — 검수용). 실패는 던진다. */
export async function getNpcLines(): Promise<NpcLineDoc[]> {
  const res = await fetch(`${adminApi()}/admin/npc-lines`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return ((await res.json()) as { lines?: NpcLineDoc[] }).lines ?? [];
}

/** 대사 저장(전체 교체) → 승인분이 아이 화면에 즉시 반영. 실패는 던진다. */
export async function saveNpcLines(
  lines: NpcLineDoc[],
): Promise<{ total: number; approved: number }> {
  const res = await fetch(`${adminApi()}/admin/npc-lines`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ lines }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return (await res.json()) as { total: number; approved: number };
}

/** AI 공급자 목록 — 굿즈·NPC 공용(키는 엣지 한 곳에 있다). 빈 배열이면 AI 버튼을 그리지 않는다. */
export const aiProviders = goodsAiProviders;

/**
 * NPC 대사 AI 초안. **저장하지 않는다** — 돌려받은 목록을 화면에서 보고 [저장]을 눌러야 반영된다.
 * 전부 pending(검수 대기)으로 오고, 위험한 문장은 엣지가 이미 버린 뒤다(dropped 로 개수 통지).
 */
export async function draftNpcLines(input: {
  zone: string;
  kinds: NpcLineKind[];
  count: number;
  pois?: string[];
  hint?: string;
  provider?: AiProvider;
  existingIds?: string[];
}): Promise<{ lines: NpcLineDoc[]; dropped: number; providerLabel: string; remaining: number }> {
  const res = await fetch(`${adminApi()}/admin/npc-lines/draft`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    // 공급자 실패 원인을 그대로 보여준다 — 운영자가 다른 공급자를 고를 수 있게.
    throw new Error(b.message ?? b.error ?? String(res.status));
  }
  return (await res.json()) as {
    lines: NpcLineDoc[];
    dropped: number;
    providerLabel: string;
    remaining: number;
  };
}

// ── QR/NFC 코드(FR-QR) — 인쇄된 종이는 회수할 수 없다. 배포 없이 목적지를 고친다 ──
export interface RedirectDoc {
  code: string;
  target: string;
  utm?: { source?: string; medium?: string; content?: string };
}

/** 코드가 몇 번 쓰였나. 코드와 **횟수**뿐 — 누가 찍었는지는 저장하지 않는다(불변식 1). */
export interface RedirectHit {
  code: string;
  n: number;
  last: string;
}

export interface RedirectsView {
  base: RedirectDoc[];
  overrides: RedirectDoc[];
  merged: RedirectDoc[];
  /** 코드별 들어온 횟수. 시험 스캔이 「이 QR 이 살아 있다」를 말해 준다. */
  hits: RedirectHit[];
  /** 🔴 **등록 안 된 코드로 들어온 것.** 인쇄물이 잘못됐다는 뜻이다 — 종이는 회수할 수 없다. */
  unknown: RedirectHit[];
  /**
   * 어느 기간을 센 것인가. `null` 이면 **처음부터 지금까지의 누계**다.
   *
   * 🔴 화면이 이걸 말해 주지 않으면 운영자는 보고서에 누계를 기간 값으로 적는다 —
   * 「8월 QR 진입 320회」라고 썼는데 사실은 개설 이후 전부인 식이다.
   */
  range: { from: string; to: string } | null;
}

/** 기본(빌드본)·덮어쓰기·병합 결과. 운영자가 "무엇이 바뀌는지" 보게 셋 다 준다. */
export async function getRedirects(period?: { from: string; to: string }): Promise<RedirectsView> {
  const q = period ? `?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}` : "";
  const res = await fetch(`${adminApi()}/admin/redirects${q}`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  const b = (await res.json()) as Partial<RedirectsView>;
  // 옛 엣지가 안 주면 빈 목록 — 화면이 죽지 않게. 「없다」와 「못 받았다」가 같아 보이는 건
  // 여기선 괜찮다(0 건이면 어차피 아무것도 안 띄운다)
  return {
    base: b.base ?? [],
    overrides: b.overrides ?? [],
    merged: b.merged ?? [],
    hits: b.hits ?? [],
    unknown: b.unknown ?? [],
    // 🔴 옛 엣지는 이 값을 모른다. 기간을 물었는데 안 오면 **누계가 온 것**이므로
    //    `null` 로 두어 화면이 「전체 기간」이라고 말하게 한다(기간을 센 척하지 않는다).
    range: b.range ?? null,
  };
}

/** 덮어쓰기 저장(전체 교체). 외부 주소는 서버가 버린다(dropped 로 개수 통지). */
export async function saveRedirects(
  entries: RedirectDoc[],
): Promise<{ count: number; dropped: number }> {
  const res = await fetch(`${adminApi()}/admin/redirects`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ entries }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return (await res.json()) as { count: number; dropped: number };
}

// ── 홈 화면 문구(FR-LAND) — 행사 주기마다 바뀌는 말을 배포 없이 ──
export async function getSiteText(): Promise<SiteText> {
  const res = await fetch(`${adminApi()}/admin/site-text`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return sanitizeSiteText(((await res.json()) as { text?: unknown }).text);
}

export async function saveSiteText(text: SiteText): Promise<SiteText> {
  const res = await fetch(`${adminApi()}/admin/site-text`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return sanitizeSiteText(((await res.json()) as { text?: unknown }).text);
}

// ── 월드 캐릭터 크기 ──
export async function getWorldSize(): Promise<WorldSize> {
  const res = await fetch(`${adminApi()}/admin/world-size`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return sanitizeWorldSize(((await res.json()) as { size?: unknown }).size);
}

export async function saveWorldSize(size: WorldSize): Promise<WorldSize> {
  const res = await fetch(`${adminApi()}/admin/world-size`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ size }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(b.message ?? String(res.status));
  }
  return sanitizeWorldSize(((await res.json()) as { size?: unknown }).size);
}

// ── 관리자 도우미(길잡이) — 읽고 안내만 한다. 고치거나 올리지 않는다 ──
export async function assistProviders(): Promise<AiProviderOption[]> {
  const res = await fetch(`${adminApi()}/admin/check`, {
    headers: { "x-admin-token": adminToken() },
  }).catch(() => null);
  if (!res?.ok) return [];
  const body = (await res.json()) as { features?: { assistProviders?: AiProviderOption[] } };
  return body.features?.assistProviders ?? [];
}

export async function askAssist(input: {
  question: string;
  state: AssistState;
  history: AssistTurn[];
  provider?: AiProvider;
}): Promise<{ answer: AssistAnswer; providerLabel: string; remaining: number }> {
  const res = await fetch(`${adminApi()}/admin/assist`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    if (b.error === "daily_limit")
      throw new Error("오늘은 도우미를 많이 불렀어요. 내일 다시 물어봐 주세요.");
    throw new Error(b.message ?? b.error ?? String(res.status));
  }
  return (await res.json()) as { answer: AssistAnswer; providerLabel: string; remaining: number };
}

/**
 * 도우미에게 넘길 "지금 상태" 요약 — "굿즈 6개 전부 스토어 주소가 비었어요" 같은 답이 나오게.
 * 실패한 항목은 그냥 빠진다(도우미가 없는 정보를 지어내지 않게 undefined 로 둔다).
 */
export async function collectAssistState(screen: string): Promise<AssistState> {
  const [manifest, slots, goods, notices, npc, rally, events] = await Promise.allSettled([
    loadManifest(),
    loadSlots(),
    loadGoodsDoc(),
    getNotices(),
    getNpcLines(),
    getRallyEvent(),
    getEvents(),
  ]);
  const st: AssistState = { screen };
  if (manifest.status === "fulfilled") st.imagesFilled = Object.keys(manifest.value.assets).length;
  if (slots.status === "fulfilled")
    st.imagesTotal = Math.max(st.imagesFilled ?? 0, slots.value.length);
  if (goods.status === "fulfilled") {
    st.goodsTotal = goods.value.products.length;
    // 옛·새 형식을 합쳐 세는 자리는 shared storeLinks 하나 — 여기서 store_url 만 보면
    // 새로 넣은 스토어가 있는데도 "준비 안 됨"으로 세어 관리자 요약만 틀린다.
    st.goodsMissingStore = goods.value.products.filter((p) => storeLinks(p).length === 0).length;
  }
  if (notices.status === "fulfilled") {
    st.noticeCount = notices.value.notices.length;
    st.hasContactUrl = !!notices.value.contactUrl;
  }
  if (npc.status === "fulfilled") {
    st.npcApproved = npc.value.filter((l) => l.status === "approved").length;
    st.npcPending = npc.value.filter((l) => l.status === "pending").length;
  }
  if (rally.status === "fulfilled") st.rallyPoints = rally.value.points?.length ?? 0;
  if (events.status === "fulfilled") st.eventCount = events.value.length;
  return st;
}

// ── 히어로 장식(모자·소품) 놓을 자리 — FR-HERO-01 ──────────────
/** 운영자가 맞춘 값. 못 받아 오면 빈 문서(=코드 기본값). */
export async function loadOverlayFit(): Promise<OverlayFitDoc> {
  try {
    const r = await fetch(`${adminApi()}/admin/overlay-fit`, { cache: "no-cache" });
    if (r.ok) {
      const b = (await r.json()) as { fit?: unknown };
      // 목록에 없는 키(운영자가 만든 모자)는 isOverlayFitKey 가 **모양으로** 통과시킨다 —
      // 그래서 여기서 매니페스트를 따로 안 넘겨도 값이 살아남는다.
      return sanitizeOverlayFitDoc(b.fit, overlayFitKeys());
    }
  } catch {
    /* 엣지 없음 — 기본값으로 편집 시작 */
  }
  return {};
}

export async function publishOverlayFit(fit: OverlayFitDoc): Promise<{ fit: OverlayFitDoc }> {
  const res = await fetch(`${adminApi()}/admin/overlay-fit`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({ fit }),
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as { fit: OverlayFitDoc };
}

/**
 * **아직 배포에 안 들어가는 그림**을 센다.
 *
 * ## 왜 필요한가
 * 관리자로 올린 파일은 **엣지의 R2 에만** 저장된다. 개발용 엣지에서 올렸다면 그건
 * `apps/edge/.wrangler/`(git 제외) 안이라 **저장소에 없다** — 배포하면 처음부터 없는 셈이다.
 * 실제로 나레이션 11개·월드 사운드 7개 등 56개(16.8MB)가 그 상태였다(docs/build/20 A1).
 *
 * 회수 도구(`pnpm art:pull`)는 있는데, **운영자는 그런 게 있는 줄 모른다.**
 * 화면에서는 멀쩡히 보이기 때문에 물어볼 이유조차 없다. 그래서 여기서 세어 알려 준다.
 *
 * 판정은 `scripts/pull-edge-assets.mjs` 와 **같은 규칙**이다 —
 * 라이브에는 있는데 정적(배포본)에는 없는 키. 두 곳이 다르면 "회수했는데 경고가 안 사라진다"가 된다.
 */
/**
 * 🖼️ **저장소(배포본) 매니페스트만** — 「새로 다듬은 그림으로 바꾸기」가 저장소에 무엇이 있는지 볼 때(2026-09-13).
 * 합친 목록(`loadManifest`)으로는 운영자가 올린 것에 가려 저장소 그림이 안 보인다.
 * 🔴 못 읽으면 `null` — 「저장소에 없다」가 아니다(단추를 안 띄울 뿐, 없다고 말하지 않는다).
 */
export async function loadRepoManifest(): Promise<Manifest | null> {
  try {
    const res = await fetch(`${contentBase()}/content/asset-manifest.json`, { cache: "no-store" });
    if (!res.ok) return null;
    const m = (await res.json()) as Manifest;
    return m && typeof m.assets === "object" ? m : null;
  } catch {
    return null; // 못 물어봤다 — 단추를 안 띄운다(호출하는 쪽은 null 을 「모름」으로 다룬다)
  }
}

export async function countPendingPull(): Promise<{ pending: string[]; unknown: boolean }> {
  const get = async (url: string): Promise<Manifest | null> => {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) return null;
      const m = (await res.json()) as Manifest;
      return m && typeof m.assets === "object" ? m : null;
    } catch {
      return null;
    }
  };
  const [edge, stat] = await Promise.all([
    get(`${adminApi()}/api/asset-manifest`),
    get(`${contentBase()}/content/asset-manifest.json`),
  ]);
  // 한쪽이라도 못 읽으면 **모른다**고 한다. 0 이라고 답하면 "괜찮다"는 거짓말이 된다.
  if (!edge || !stat) return { pending: [], unknown: true };

  const base = stat.assets ?? {};
  const pending = Object.entries(edge.assets ?? {})
    .filter(([k, e]) => !base[k] && e?.url)
    .map(([k]) => k);
  return { pending, unknown: false };
}

// ── 🐱 AI 도슨트: NPC 설정 · 자료 묶음 ──────────────────────────
// 저장 실패는 **던진다** — 조용히 성공한 척하면 운영자는 저장된 줄 알고 창을 닫는다.
// 401 은 authFail() 로 열쇠 다시 넣기 안내를 띄운다(다른 화면과 같은 방식).

export async function getNpcAgents(): Promise<NpcAgentDoc> {
  const res = await fetch(`${adminApi()}/admin/npc-agents`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  return normalizeAgentDoc(await res.json());
}

/**
 * 🗒️ 자유 대화로 들어온 질문(30일) — FR-NPC-05. 표가 없으면(마이그레이션 전) 서버가 「무엇을 적용하라」를 준다 —
 * 그 말을 그대로 던진다(「연결 못 했다」로 뭉개지 않는다).
 */
export async function getNpcLog(npc: string): Promise<NpcChatLogRow[]> {
  const res = await fetch(`${adminApi()}/admin/npc-log?npc=${encodeURIComponent(npc)}`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { reason?: string };
    throw new Error(body.reason ?? `들어온 질문을 못 불러왔어요(${res.status})`);
  }
  return sanitizeChatLogRows(await res.json());
}

/** 한 줄 지우기 — 이상한 글을 30일 기다리지 않고 바로 지운다 */
export async function deleteNpcLog(id: number): Promise<void> {
  const res = await fetch(`${adminApi()}/admin/npc-log?id=${id}`, {
    method: "DELETE",
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(`못 지웠어요(${res.status})`);
}

export async function saveNpcAgents(
  doc: NpcAgentDoc,
): Promise<{ agents: number; open: number; pending: number }> {
  const res = await fetch(`${adminApi()}/admin/npc-agents`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(doc),
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? String(res.status));
  }
  return (await res.json()) as { agents: number; open: number; pending: number };
}

export async function getAiKb(): Promise<KbPackDoc> {
  const res = await fetch(`${adminApi()}/admin/ai-kb`, {
    headers: { "x-admin-token": adminToken() },
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) throw new Error(String(res.status));
  return normalizeKbPackDoc(await res.json());
}

export async function saveAiKb(doc: KbPackDoc): Promise<{ packs: number; chunks: number }> {
  const res = await fetch(`${adminApi()}/admin/ai-kb`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(doc),
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? String(res.status));
  }
  return (await res.json()) as { packs: number; chunks: number };
}

/**
 * 🤖 문답 초안 만들기 — 자료를 읽고 「무엇을 물어보면 뭐라 답할지」를 뽑는다.
 *
 * **자료 내용은 안 보낸다** — 어느 묶음을 쓸지(id)만 보내고 내용은 서버가 D1 에서 꺼낸다.
 * 본문으로 자료를 실어 보내면 그게 곧 프롬프트 주입 경로가 된다(불변식 7).
 */
export async function draftNpcQa(input: {
  role: string;
  packs: string[];
  count: number;
  npcLabel?: string;
  hint?: string;
  provider?: AiProvider;
  existingIds?: string[];
}): Promise<{ qa: NpcQa[]; dropped: number; providerLabel: string; remaining: number }> {
  const res = await fetch(`${adminApi()}/admin/npc-agents/draft`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    // 공급자 실패 원인을 그대로 보여준다 — 운영자가 다른 공급자를 고를 수 있게.
    throw new Error(b.message ?? b.error ?? String(res.status));
  }
  return (await res.json()) as {
    qa: NpcQa[];
    dropped: number;
    providerLabel: string;
    remaining: number;
  };
}

/** 🌏 승인된 문답을 다른 말로. 옮긴 것도 **전부 검수 대기**로 온다. */
export async function translateNpcQa(input: {
  npc: string;
  lang: string;
  ids?: string[];
  provider?: AiProvider;
}): Promise<{ qa: NpcQa[]; dropped: number; skipped: number; providerLabel: string }> {
  const res = await fetch(`${adminApi()}/admin/npc-agents/translate`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify(input),
  });
  if (res.status === 401) throw authFail();
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(b.message ?? b.error ?? String(res.status));
  }
  return (await res.json()) as {
    qa: NpcQa[];
    dropped: number;
    skipped: number;
    providerLabel: string;
  };
}

/**
 * 🎨 **그림 초안 받기** — 말로 적은 것을 후보 그림 몇 장으로.
 *
 * 초안이지 게시가 아니다(docs/build/00 S4). 받은 그림은 화면에 보여 주기만 하고,
 * 저장은 운영자가 고른 뒤 **[파일 고르기]와 같은 길**로 간다 — 규격 검사·자동 줄이기·
 * 업로드가 전부 그 길에 이미 있다.
 */
export async function generateArt(
  kind: string,
  what: string,
  opts: { style?: string; n?: number; prompt?: string; refs?: string[] } = {},
): Promise<{ images: string[]; left: number }> {
  const res = await fetch(`${adminApi()}/admin/art`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-token": adminToken() },
    body: JSON.stringify({
      kind,
      what,
      style: opts.style,
      n: opts.n,
      prompt: opts.prompt,
      refs: opts.refs,
    }),
  }).catch(() => {
    throw new Error("서버에 연결하지 못했어요. 잠시 뒤 다시 눌러 주세요.");
  });
  if (res.status === 401) throw authFail();
  if (res.ok) return (await res.json()) as { images: string[]; left: number };
  // 엣지가 사람 말 메시지를 준다(키 없음·하루 상한·너무 김 등) — 그대로 보여 준다
  const msg = await res
    .json()
    .then((b) => (b as { message?: string }).message)
    .catch(() => undefined);
  throw new Error(msg || `그림을 만들지 못했어요(${res.status})`);
}

/**
 * 🔎 **붙여넣은 화상 주소를 서버가 한 번 열어 본다.**
 *
 * 브라우저는 교차출처 응답 헤더를 못 읽어서 「이 서비스가 끼워 넣기를 막았나」를 모른다.
 * 그래서 엣지가 대신 열어 보고 **운영자 말**로 돌려준다(판정은 shared `meetVerdict`).
 *
 * 🔴 못 물어봤으면 **아무 말도 안 한다** — 「없다」로 단정하면 멀쩡한 주소를 고치게 만든다.
 */
export async function checkMeetUrl(url: string): Promise<MeetVerdict | null> {
  const res = await fetch(
    `${adminApi()}/admin/meet/check?url=${encodeURIComponent(url)}`,
    { headers: { "x-admin-token": adminToken() } },
  ).catch(() => null);
  if (!res?.ok) return null;
  return (await res.json()) as MeetVerdict;
}
